(() => {
  const COLS = 52;
  const ROWS = 16;
  const EMPTY = '';
  const DEFAULT_PATTERN_COLOR = '#d70252';

  const GLYPHS = {
    U: ['10001', '10001', '10001', '10001', '10001', '10001', '01110'],
    L: ['10000', '10000', '10000', '10000', '10000', '10000', '11111'],
    A: ['01110', '10001', '10001', '11111', '10001', '10001', '10001'],
    N: ['10001', '11001', '10101', '10011', '10001', '10001', '10001'],
    Z: ['11111', '00010', '00100', '01000', '10000', '10000', '11111'],
    I: ['11111', '00100', '00100', '00100', '00100', '00100', '11111']
  };

  const buildUlanziPattern = () => {
    const word = 'ULANZI';
    const letterW = 5;
    const letterH = 7;
    const gap = 2;
    const totalW = word.length * letterW + (word.length - 1) * gap;
    const startX = Math.floor((COLS - totalW) / 2);
    const startY = Math.floor((ROWS - letterH) / 2);
    const cells = [];

    word.split('').forEach((ch, i) => {
      const glyph = GLYPHS[ch];
      if (!glyph) return;
      const ox = startX + i * (letterW + gap);
      glyph.forEach((row, y) => {
        for (let x = 0; x < row.length; x += 1) {
          if (row[x] === '1') cells.push([ox + x, startY + y]);
        }
      });
    });

    return cells;
  };

  const ULANZI_CELLS = buildUlanziPattern();
  const MOTION_ATTR = 'data-pc-motion';
  const RETRY_MS = 80;
  const RETRY_MAX = 40;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initCanvas = (root) => {
    if (!root || root._pcReady) return;
    const board = root.querySelector('[data-pc-board]');
    if (!board || !board.getContext) return;

    root._pcReady = true;
    const ctx = board.getContext('2d');
    const swatches = Array.from(root.querySelectorAll('[data-pc-swatch]'));
    const toolButtons = Array.from(root.querySelectorAll('[data-pc-tool]'));
    const clearBtn = root.querySelector('[data-pc-clear]');
    const presetButtons = Array.from(root.querySelectorAll('[data-pc-preset]'));
    const library = root.querySelector('[data-pc-library]');
    const scrollButtons = Array.from(root.querySelectorAll('[data-pc-scroll]'));

    let grid = Array.from({ length: ROWS }, () => Array(COLS).fill(EMPTY));
    let color =
      (swatches.find((s) => s.classList.contains('is-active')) || swatches[0] || {}).getAttribute(
        'data-pc-color'
      ) || DEFAULT_PATTERN_COLOR;
    let tool = 'brush';
    let painting = false;
    let lastX = -1;
    let lastY = -1;
    let cellW = 1;
    let cellH = 1;
    let cssW = 1;
    let cssH = 1;
    let libraryDragStartX = 0;
    let libraryDragStartScroll = 0;
    let libraryDragging = false;
    let libraryDragMoved = false;
    let suppressLibraryClick = false;

    const paintValue = () => (tool === 'eraser' ? EMPTY : color);

    const setActiveSwatch = (swatch) => {
      swatches.forEach((item) => {
        const active = item === swatch;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      if (swatch) color = swatch.getAttribute('data-pc-color') || color;
    };

    const setTool = (nextTool) => {
      tool = nextTool === 'eraser' ? 'eraser' : 'brush';
      toolButtons.forEach((button) => {
        const selected = button.getAttribute('data-pc-tool') === tool;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      board.classList.toggle('is-erasing', tool === 'eraser');
    };

    const resize = () => {
      const rect = board.getBoundingClientRect();
      cssW = Math.max(1, rect.width);
      cssH = Math.max(1, rect.height);
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      board.width = Math.round(cssW * dpr);
      board.height = Math.round(cssH * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cellW = cssW / COLS;
      cellH = cssH / ROWS;
      draw();
    };

    const draw = () => {
      ctx.clearRect(0, 0, cssW, cssH);
      ctx.fillStyle = '#141414';
      ctx.fillRect(0, 0, cssW, cssH);

      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 1;
      for (let x = 0; x <= COLS; x += 1) {
        const px = Math.round(x * cellW) + 0.5;
        ctx.beginPath();
        ctx.moveTo(px, 0);
        ctx.lineTo(px, cssH);
        ctx.stroke();
      }
      for (let y = 0; y <= ROWS; y += 1) {
        const py = Math.round(y * cellH) + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, py);
        ctx.lineTo(cssW, py);
        ctx.stroke();
      }

      for (let y = 0; y < ROWS; y += 1) {
        for (let x = 0; x < COLS; x += 1) {
          const c = grid[y][x];
          if (!c) continue;
          ctx.fillStyle = c;
          ctx.fillRect(
            Math.floor(x * cellW) + 1,
            Math.floor(y * cellH) + 1,
            Math.max(1, Math.ceil(cellW) - 1),
            Math.max(1, Math.ceil(cellH) - 1)
          );
        }
      }
    };

    const cellFromPoint = (clientX, clientY) => {
      const rect = board.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      const x = Math.floor(((clientX - rect.left) / rect.width) * COLS);
      const y = Math.floor(((clientY - rect.top) / rect.height) * ROWS);
      if (x < 0 || y < 0 || x >= COLS || y >= ROWS) return null;
      return { x, y };
    };

    const paintLine = (x0, y0, x1, y1) => {
      const value = paintValue();
      let x = x0;
      let y = y0;
      const dx = Math.abs(x1 - x0);
      const dy = Math.abs(y1 - y0);
      const sx = x0 < x1 ? 1 : -1;
      const sy = y0 < y1 ? 1 : -1;
      let err = dx - dy;
      let changed = false;

      while (true) {
        if (grid[y][x] !== value) {
          grid[y][x] = value;
          changed = true;
        }
        if (x === x1 && y === y1) break;
        const e2 = err * 2;
        if (e2 > -dy) {
          err -= dy;
          x += sx;
        }
        if (e2 < dx) {
          err += dx;
          y += sy;
        }
      }

      return changed;
    };

    const paintAt = (clientX, clientY) => {
      const cell = cellFromPoint(clientX, clientY);
      if (!cell) return;
      const value = paintValue();
      let changed = false;

      if (lastX < 0 || lastY < 0) {
        if (grid[cell.y][cell.x] !== value) {
          grid[cell.y][cell.x] = value;
          changed = true;
        }
      } else if (lastX !== cell.x || lastY !== cell.y) {
        changed = paintLine(lastX, lastY, cell.x, cell.y);
      }

      lastX = cell.x;
      lastY = cell.y;
      if (changed) draw();
    };

    const paintDefault = () => {
      grid = Array.from({ length: ROWS }, () => Array(COLS).fill(EMPTY));
      ULANZI_CELLS.forEach(([x, y]) => {
        if (y >= 0 && y < ROWS && x >= 0 && x < COLS) grid[y][x] = DEFAULT_PATTERN_COLOR;
      });
      draw();
    };

    const clearPresets = () => {
      presetButtons.forEach((button) => button.classList.remove('is-active'));
    };

    const applySampledPixels = (image) => {
      const sampler = document.createElement('canvas');
      sampler.width = COLS;
      sampler.height = ROWS;
      const samplerCtx = sampler.getContext('2d', { willReadFrequently: true });
      if (!samplerCtx) return false;

      samplerCtx.imageSmoothingEnabled = false;
      samplerCtx.drawImage(image, 0, 0, COLS, ROWS);

      let sampled;
      try {
        sampled = samplerCtx.getImageData(0, 0, COLS, ROWS).data;
      } catch (err) {
        return false;
      }

      if (sampled.length !== COLS * ROWS * 4) return false;

      for (let y = 0; y < ROWS; y += 1) {
        for (let x = 0; x < COLS; x += 1) {
          const index = (y * COLS + x) * 4;
          grid[y][x] =
            sampled[index + 3] < 16
              ? EMPTY
              : `rgb(${sampled[index]}, ${sampled[index + 1]}, ${sampled[index + 2]})`;
        }
      }

      draw();
      return true;
    };

    const loadPresetImage = (src) =>
      new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });

    const importPreset = (button) => {
      if (!button) return;
      const thumb = button.querySelector('img');
      const src = button.getAttribute('data-pc-preset-src') || (thumb && (thumb.currentSrc || thumb.src));

      const finish = (ok) => {
        if (!ok) {
          console.warn('[pixel-canvas] preset image failed to apply');
          return;
        }
        presetButtons.forEach((item) => item.classList.toggle('is-active', item === button));
      };

      if (thumb && thumb.complete && thumb.naturalWidth > 0 && applySampledPixels(thumb)) {
        finish(true);
        return;
      }

      if (!src) {
        finish(false);
        return;
      }

      fetch(src, { mode: 'cors', credentials: 'omit' })
        .then((res) => {
          if (!res.ok) throw new Error('preset fetch failed');
          return res.blob();
        })
        .then((blob) => createImageBitmap(blob))
        .then((bitmap) => {
          const ok = applySampledPixels(bitmap);
          if (bitmap.close) bitmap.close();
          finish(ok);
        })
        .catch(() =>
          loadPresetImage(src)
            .then((image) => finish(applySampledPixels(image)))
            .catch(() => finish(false))
        );
    };

    const presetFromEvent = (event) => {
      const node = event.target && event.target.closest ? event.target.closest('[data-pc-preset]') : null;
      if (node && root.contains(node)) return node;
      if (!event.clientX && event.clientX !== 0) return null;
      const under = document.elementFromPoint(event.clientX, event.clientY);
      const fallback = under && under.closest ? under.closest('[data-pc-preset]') : null;
      return fallback && root.contains(fallback) ? fallback : null;
    };

    const updateLibraryScrollButtons = () => {
      if (!library) return;
      const maxScroll = Math.max(0, library.scrollWidth - library.clientWidth);
      scrollButtons.forEach((button) => {
        const direction = Number(button.getAttribute('data-pc-scroll') || 0);
        button.disabled = direction < 0 ? library.scrollLeft <= 2 : library.scrollLeft >= maxScroll - 2;
      });
    };

    const onPointerDown = (event) => {
      painting = true;
      lastX = -1;
      lastY = -1;
      board.setPointerCapture(event.pointerId);
      paintAt(event.clientX, event.clientY);
    };

    const onPointerMove = (event) => {
      if (!painting) return;
      paintAt(event.clientX, event.clientY);
    };

    const onPointerUp = (event) => {
      painting = false;
      lastX = -1;
      lastY = -1;
      try {
        board.releasePointerCapture(event.pointerId);
      } catch (e) {
        /* ignore */
      }
    };

    const onClear = () => {
      grid = Array.from({ length: ROWS }, () => Array(COLS).fill(EMPTY));
      clearPresets();
      draw();
    };

    const onSwatchClick = (event) => {
      setActiveSwatch(event.currentTarget);
      setTool('brush');
    };

    const onToolClick = (event) => {
      setTool(event.currentTarget.getAttribute('data-pc-tool') || 'brush');
    };

    const onPresetClick = (event) => {
      if (libraryDragMoved || suppressLibraryClick) {
        event.preventDefault();
        return;
      }
      importPreset(event.currentTarget);
    };

    const onLibraryClick = (event) => {
      if (libraryDragMoved || suppressLibraryClick) return;
      const button = presetFromEvent(event);
      if (button) importPreset(button);
    };

    const onScrollClick = (event) => {
      if (!library) return;
      const direction = Number(event.currentTarget.getAttribute('data-pc-scroll') || 0);
      library.scrollBy({
        left: direction * library.clientWidth * 0.72,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    };

    const onLibraryPointerDown = (event) => {
      if (event.button !== 0) return;
      libraryDragStartX = event.clientX;
      libraryDragStartScroll = library.scrollLeft;
      libraryDragging = true;
      libraryDragMoved = false;
    };

    const onLibraryPointerMove = (event) => {
      if (!libraryDragging) return;
      const distance = event.clientX - libraryDragStartX;
      if (!libraryDragMoved && Math.abs(distance) > 6) {
        libraryDragMoved = true;
        library.classList.add('is-dragging');
        try {
          library.setPointerCapture(event.pointerId);
        } catch (e) {
          /* ignore */
        }
      }
      if (!libraryDragMoved) return;
      event.preventDefault();
      library.scrollLeft = libraryDragStartScroll - distance;
    };

    const finishLibraryDrag = (event) => {
      if (!libraryDragging) return;
      const moved = libraryDragMoved;
      const button = moved ? null : presetFromEvent(event);
      libraryDragging = false;
      library.classList.remove('is-dragging');
      if (library.hasPointerCapture && library.hasPointerCapture(event.pointerId)) {
        library.releasePointerCapture(event.pointerId);
      }
      if (!moved && button) importPreset(button);
      suppressLibraryClick = true;
      window.setTimeout(() => {
        suppressLibraryClick = false;
      }, 0);
      updateLibraryScrollButtons();
    };

    swatches.forEach((swatch) => swatch.addEventListener('click', onSwatchClick));
    toolButtons.forEach((button) => button.addEventListener('click', onToolClick));
    presetButtons.forEach((button) => button.addEventListener('click', onPresetClick));
    scrollButtons.forEach((button) => button.addEventListener('click', onScrollClick));
    if (clearBtn) clearBtn.addEventListener('click', onClear);

    if (library) {
      library.addEventListener('click', onLibraryClick);
      library.addEventListener('pointerdown', onLibraryPointerDown);
      library.addEventListener('pointermove', onLibraryPointerMove);
      library.addEventListener('pointerup', finishLibraryDrag);
      library.addEventListener('pointercancel', finishLibraryDrag);
      library.addEventListener('scroll', updateLibraryScrollButtons, { passive: true });
    }

    const onBlockSelect = (event) => {
      const blockRoot = event.target;
      if (!blockRoot || !root.contains(blockRoot)) return;
      const button =
        blockRoot.matches && blockRoot.matches('[data-pc-preset]')
          ? blockRoot
          : blockRoot.querySelector && blockRoot.querySelector('[data-pc-preset]');
      if (button) importPreset(button);
    };
    document.addEventListener('shopify:block:select', onBlockSelect);

    board.addEventListener('pointerdown', onPointerDown);
    board.addEventListener('pointermove', onPointerMove);
    board.addEventListener('pointerup', onPointerUp);
    board.addEventListener('pointercancel', onPointerUp);
    board.addEventListener('lostpointercapture', () => {
      painting = false;
      lastX = -1;
      lastY = -1;
    });

    window.addEventListener('resize', updateLibraryScrollButtons);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    if (ro) ro.observe(board);
    else window.addEventListener('resize', resize);

    root._pcCleanup = () => {
      swatches.forEach((swatch) => swatch.removeEventListener('click', onSwatchClick));
      toolButtons.forEach((button) => button.removeEventListener('click', onToolClick));
      presetButtons.forEach((button) => button.removeEventListener('click', onPresetClick));
      scrollButtons.forEach((button) => button.removeEventListener('click', onScrollClick));
      if (clearBtn) clearBtn.removeEventListener('click', onClear);
      if (library) {
        library.removeEventListener('click', onLibraryClick);
        library.removeEventListener('pointerdown', onLibraryPointerDown);
        library.removeEventListener('pointermove', onLibraryPointerMove);
        library.removeEventListener('pointerup', finishLibraryDrag);
        library.removeEventListener('pointercancel', finishLibraryDrag);
        library.removeEventListener('scroll', updateLibraryScrollButtons);
      }
      document.removeEventListener('shopify:block:select', onBlockSelect);
      board.removeEventListener('pointerdown', onPointerDown);
      board.removeEventListener('pointermove', onPointerMove);
      board.removeEventListener('pointerup', onPointerUp);
      board.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('resize', updateLibraryScrollButtons);
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', resize);
      root._pcReady = false;
      root._pcCleanup = null;
      root._pcPaintDefault = null;
      root._pcBootTo = null;
    };

    root._pcPaintDefault = paintDefault;
    root._pcBootTo = (count) => {
      const n = Math.min(ULANZI_CELLS.length, Math.max(0, Math.floor(count)));
      grid = Array.from({ length: ROWS }, () => Array(COLS).fill(EMPTY));
      for (let i = 0; i < n; i += 1) {
        const [x, y] = ULANZI_CELLS[i];
        if (y >= 0 && y < ROWS && x >= 0 && x < COLS) grid[y][x] = DEFAULT_PATTERN_COLOR;
      }
      draw();
    };

    if (swatches[0]) setActiveSwatch(swatches[0]);
    setTool('brush');
    resize();
    if (reduceMotion) paintDefault();
    else draw();
    updateLibraryScrollButtons();

    if (ULANZI_CELLS.some(([x, y]) => x < 0 || y < 0 || x >= COLS || y >= ROWS)) {
      console.warn('[pixel-canvas] Ulanzi pattern out of bounds');
    }
  };

  const destroyMotion = (root) => {
    if (!root) return;
    if (root._pcMotionRetry) {
      window.clearTimeout(root._pcMotionRetry);
      root._pcMotionRetry = null;
    }
    if (root._pcCtx) {
      root._pcCtx.revert();
      root._pcCtx = null;
    }
    if (root.querySelector('[data-pc-board]')) {
      root.querySelector('[data-pc-board]').style.pointerEvents = '';
    }
    root.removeAttribute(MOTION_ATTR);
  };

  const setupMotion = (root, attempt) => {
    if (!root || root.hasAttribute(MOTION_ATTR)) return;
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      if ((attempt || 0) < RETRY_MAX) {
        root._pcMotionRetry = window.setTimeout(() => setupMotion(root, (attempt || 0) + 1), RETRY_MS);
      } else if (typeof root._pcPaintDefault === 'function') {
        root._pcPaintDefault();
      }
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    root.setAttribute(MOTION_ATTR, '');

    if (reduceMotion) {
      if (typeof root._pcPaintDefault === 'function') root._pcPaintDefault();
      return;
    }

    const tag = root.querySelector('[data-pc-tag]');
    const title = root.querySelector('[data-pc-title]');
    const body = root.querySelector('[data-pc-body]');
    const device = root.querySelector('[data-pc-device]');
    const tools = root.querySelectorAll('[data-pc-tool]');
    const swatches = root.querySelectorAll('[data-pc-swatch]');
    const clearBtn = root.querySelector('[data-pc-clear]');
    const libLabel = root.querySelector('[data-pc-library-label]');
    const libShell = root.querySelector('[data-pc-library-shell]');
    const hint = root.querySelector('[data-pc-hint]');
    const board = root.querySelector('[data-pc-board]');

    root._pcCtx = gsap.context(() => {
      if (tag) gsap.set(tag, { autoAlpha: 0, y: 16 });
      if (title) gsap.set(title, { autoAlpha: 0, y: 20 });
      if (body) gsap.set(body, { autoAlpha: 0, y: 16 });
      if (device) {
        gsap.set(device, {
          autoAlpha: 0,
          y: 36,
          scale: 0.97,
          transformOrigin: '50% 80%',
          force3D: true
        });
      }
      if (tools.length) gsap.set(tools, { autoAlpha: 0, x: -16, y: 8 });
      if (swatches.length) gsap.set(swatches, { autoAlpha: 0, scale: 0.6 });
      if (clearBtn) gsap.set(clearBtn, { autoAlpha: 0, y: 10 });
      if (libLabel) gsap.set(libLabel, { autoAlpha: 0, y: 12 });
      if (libShell) gsap.set(libShell, { autoAlpha: 0, y: 20 });
      if (hint) gsap.set(hint, { autoAlpha: 0, y: 10 });
      if (board) board.style.pointerEvents = 'none';

      const boot = { n: 0 };
      const tl = gsap.timeline({
        defaults: { ease: 'power3.out' },
        scrollTrigger: {
          trigger: root,
          start: 'top 78%',
          once: true,
          toggleActions: 'play none none none'
        }
      });

      if (tag) tl.to(tag, { autoAlpha: 1, y: 0, duration: 0.65 }, 0);
      if (title) tl.to(title, { autoAlpha: 1, y: 0, duration: 0.8 }, 0.1);
      if (body) tl.to(body, { autoAlpha: 1, y: 0, duration: 0.7 }, 0.22);
      if (device) tl.to(device, { autoAlpha: 1, y: 0, scale: 1, duration: 0.95 }, 0.28);
      if (tools.length) tl.to(tools, { autoAlpha: 1, x: 0, y: 0, duration: 0.55, stagger: 0.1 }, 0.42);

      tl.to(
        boot,
        {
          n: ULANZI_CELLS.length,
          duration: 0.85,
          ease: 'none',
          onUpdate: () => {
            if (typeof root._pcBootTo === 'function') root._pcBootTo(boot.n);
          },
          onComplete: () => {
            if (board) board.style.pointerEvents = '';
          }
        },
        0.85
      );

      if (swatches.length) tl.to(swatches, { autoAlpha: 1, scale: 1, duration: 0.45, stagger: 0.05 }, 1.15);
      if (clearBtn) tl.to(clearBtn, { autoAlpha: 1, y: 0, duration: 0.45 }, 1.3);
      if (libLabel) tl.to(libLabel, { autoAlpha: 1, y: 0, duration: 0.5 }, 1.42);
      if (libShell) tl.to(libShell, { autoAlpha: 1, y: 0, duration: 0.7 }, 1.5);
      if (hint) tl.to(hint, { autoAlpha: 1, y: 0, duration: 0.5 }, 1.65);
    }, root);
  };

  const scan = () => {
    document.querySelectorAll('[data-pc]').forEach((root) => {
      initCanvas(root);
      setupMotion(root, 0);
    });
  };

  const rescan = () => {
    document.querySelectorAll('[data-pc]').forEach((root) => {
      destroyMotion(root);
      if (typeof root._pcCleanup === 'function') root._pcCleanup();
    });
    scan();
    if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', rescan);
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target && event.target.querySelector('[data-pc]');
    destroyMotion(root);
    if (root && typeof root._pcCleanup === 'function') root._pcCleanup();
  });
})();
